import { Component, OnInit, HostListener, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { Constant } from '../constant';
import { CommonUtil } from '../util/common-util';
import { ToastUtil } from '../util/toast-util';
import { MyEvent, MyEventApproval } from '../model/my-event';
import { Auth } from '@aws-amplify/auth';
import { NgxPaginationModule } from 'ngx-pagination';
import {
  MasterItem,
} from '../model/my-event';
import { EventSearchFilterComponent } from '../event-search-filter/event-search-filter.component';
import { EventSearchCondition, EventSearchOrder, EventSearchRequest } from '../model/event-search';

@Component({
  selector: 'app-event-search',
  templateUrl: './event-search.component.html',
  styleUrl: './event-search.component.css',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    NgxPaginationModule,
    EventSearchFilterComponent
  ]
})
export class EventSearchComponent implements OnInit, OnDestroy {
  public apiPath = '/event';
  public readonly Constant = Constant;
  public showSpinner = false;

  // タブ
  public activeTab: 'draft' | 'approval' = 'draft';

  // フィルター
  public showCompleted = false;

  // 起票イベントリスト
  public draftEvents: MyEvent[] = [];

  // 承認イベントリスト
  public approvalEvents: MyEventApproval[] = [];
  public filteredApprovalEvents: MyEventApproval[] = [];
  public filteredDraftEvents: MyEvent[] = [];

  private routeSub: Subscription;

  //targetSelectedNodes: any[] = [];

  private orgMasterData: any = {};
  public industryLarges;
  public employeeSizes;

  public MasterData: { [key: string]: MasterItem[] } = {};
  public masterLoaded = false;
  private page = 1;
  private hasMore = true;
  private isLoading = false;
  private pendingReload = false;
  private destroyed = false;
  public totalCount = 0;

  // 絞込・ソート条件
  public searchConditions: EventSearchCondition[] = [];
  public searchOrder: EventSearchOrder = Constant.esOrderNewest;

  config = {
    id: 'approvalEvents',
    currentPage: 1,
    itemsPerPage: Constant.evPageSize,
    totalItems: 0
  };

  constructor(
    private title: Title,
    private router: Router,
    private route: ActivatedRoute,
    private commonUtil: CommonUtil,
    private toastUtil: ToastUtil,
    private dialog: MatDialog,
    private auth: AuthService,
  ) {
    this.title.setTitle(Constant.pageTitleSearchEvent + Constant.pageTitleCommon);
    this.industryLarges = [
      { label: '情報通信・IT', value: '0' },
      { label: '製造', value: '1' },
      { label: 'インフラ・建設・運輸', value: '2' },
      { label: '流通', value: '3' },
      { label: 'サービス', value: '4' },
      { label: '金融', value: '5' },
      { label: '医療・ライフサイエンス', value: '6' },
      { label: '公共・教育・研究', value: '7' },
      { label: '農林水産・鉱業', value: '8' },
      { label: 'その他', value: '9' }
    ];
    this.employeeSizes = [
      { label: '〜50人', value: '0' },
      { label: '51〜100人', value: '1' },
      { label: '101〜500人', value: '2' },
      { label: '501〜1,000人', value: '3' },
      { label: '1,001〜3,000人', value: '4' },
      { label: '3,001〜5,000人', value: '5' },
      { label: '5,001〜7,000人', value: '6' },
      { label: '7,001〜9,000人', value: '7' },
      { label: '9,001〜11,000人', value: '8' },
      { label: '11,001〜13,000人', value: '9' },
      { label: '13,001〜15,000人', value: '10' },
      { label: '15,001人〜', value: '11' }
    ];
  }

  @HostListener('window:focus')
  onFocus(): void {
    this.commonUtil.checkRefreshToken(this.auth);
  }

  ngOnInit() {
    window.scroll(0, 0);

    // URLパスに基づいてタブを決定
    this.routeSub = this.route.url.subscribe(segments => {
      const path = segments.map(s => s.path).join('/');
      this.loadMasterData();
      this.loadEvents();
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
  }

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.config.itemsPerPage);
  }

  get displayFrom(): number {
    if (this.totalCount === 0) {
      return 0;
    }
    return (this.page - 1) * this.config.itemsPerPage + 1;
  }

  get displayTo(): number {
    return Math.min(this.page * this.config.itemsPerPage, this.totalCount);
  }

  // 絞込・ソート条件変更（子コンポーネントからの通知）
  onFilterSearch(request: EventSearchRequest): void {
    this.searchConditions = request.conditions;
    this.searchOrder = request.order;
    this.page = 1;
    this.config.currentPage = 1;
    window.scroll(0, 0);
    this.loadEvents();
  }

  get pages(): number[] {
    const maxSize = 11;
    const total = this.totalPages;

    if (total <= maxSize) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    let start = Math.max(1, this.page - Math.floor(maxSize / 2));
    let end = start + maxSize - 1;

    if (end > total) {
      end = total;
      start = end - maxSize + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  isFirstPage(): boolean {
    return this.page === 1;
  }

  isLastPage(): boolean {
    return this.page >= this.totalPages;
  }

  goPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.page) {
      return;
    }

    this.page = page;
    this.config.currentPage = page;

    window.scroll(0, 0);
    this.loadEvents();
  }

  previous(): void {
    this.goPage(this.page - 1);
  }

  next(): void {
    this.goPage(this.page + 1);
  }

  getIndustryLargeLabel(value: string | number): string {
    return this.industryLarges.find(x => x.value === String(value))?.label ?? '';
  }

  getEmployeeSizeLabel(value: string | number): string {
    return this.employeeSizes.find(x => x.value === String(value))?.label ?? '';
  }

  getMasterName(type: string, id: number | null | undefined): string {
    if (id == null) return '';
    return this.getPlanMasterItems(type).find(m => m.id === id)?.name ?? '';
  }

  private getPlanMasterItems(type: string): MasterItem[] {
    return this.MasterData[type] || [];
  }

  // イベント詳細へ遷移（承認タブ）
  onEventClick(event: MyEventApproval) {
    window.open(`/event/${event.id}`, '_blank');
  }

  onEventEXClick(event: MyEventApproval) {
    window.open(`/ex-event/${event.id}`, '_blank');
  }

  getCollectionRate(event: MyEventApproval): string {
    if (!event.evalCollectedCount || !event.participantTotal) return '- %';
    return Math.round(event.evalCollectedCount / event.participantTotal * 100) + '%';
  }

  // 概要の省略表示（121文字以上で省略）
  /** 概要を maxLength 文字までにする（超える分は「...」）。表は1440px以上で116文字、1024〜1439pxで50文字 */
  getTruncatedDescription(description: string, maxLength = 116): string {
    if (!description) return '';
    if (description.length <= maxLength) return description.replace(/\n/g, ' ');
    return description.replace(/\n/g, ' ').substring(0, maxLength) + '...';
  }

  // 開催日の表示（最大3つ + など）
  getDisplayDates(event: MyEvent | MyEventApproval): string[] {

    // schedules がある場合
    if (event.schedules?.length) {

      const uniqueDates = [
        ...new Set(event.schedules.map(s => s.eventDate))
      ].sort();

      // 期間表示
      if (event.numberOfDays === 'period') {

        const start = uniqueDates[0];
        const end = uniqueDates[uniqueDates.length - 1];

        return [
          `${this.getFormattedDate(start)}\n〜\n${this.getFormattedDate(end)}`
        ];
      }

      // 通常表示（最大3件）
      return uniqueDates
        .slice(0, 3)
        .map(date => this.getFormattedDate(date));
    }

    // eventDates fallback
    if (event.eventDates?.length) {

      if (event.numberOfDays === 'period') {

        return [
          `${this.getFormattedDate(event.eventDates[0])}\n〜\n${
            this.getFormattedDate(
              event.eventDates[event.eventDates.length - 1]
            )
          }`
        ];
      }

      return event.eventDates
        .slice(0, 3)
        .map(date => this.getFormattedDate(date));
    }

    return [];
  }

  // 帳票種類の表示
  getDocumentTypeLabel(
    type: 'plan' | 'report',
    status: string
  ): string {

    // reportでもcompleted以外は企画として表示
    if (type === 'report' && status !== 'completed') {
      return Constant.myEventDocumentTypes['plan'];
    }

    return Constant.myEventDocumentTypes[type];
  }

  getFormattedDate(date: string): string {
    if (!date) return '';
    return date.replace(/-/g, '/');
  }

  private loadEvents() {

    // 検索中に絞込・並び替えが変更された場合、取りこぼさず現在の検索完了後に
    // 最新の条件で再検索する（条件チップの表示と検索結果がずれるのを防ぐ）
    if (this.isLoading) {
      this.pendingReload = true;
      return;
    }
    if (!this.commonUtil.checkOnline()) return;

    this.isLoading = true;
    this.showSpinner = true;

    Auth.currentSession().then(session => {

      const options: any = this.auth.createApiHeader(session);
      options['body'] = {
        page: this.page - 1,
        size: Constant.evPageSize,
        conditions: this.searchConditions,
        order: this.searchOrder,
      };

      this.commonUtil.apiPost(this.apiPath, options)
        .then(res => {

          this.approvalEvents = res.data.items;
          this.totalCount = res.data.total;

          this.config.totalItems = res.data.total;

          this.showSpinner = false;
          this.isLoading = false;
          this.reloadIfPending();

        })
        .catch(err => {

          this.commonUtil.debug().log(err);

          this.showSpinner = false;
          this.isLoading = false;

          if (err.message === Constant.msgLoginFailed) {
            this.commonUtil.showReloadDialog(this.auth);
          } else {
            this.toastUtil.clearAllShowingToast();
            this.toastUtil.showErrorToast('', Constant.msgNetworkError, Constant.toastShowMiliSec);
          }

          this.reloadIfPending();

        });

    });

  }

  // 検索中に取りこぼした再検索要求があれば、最新の条件で1回だけ実行する
  private reloadIfPending(): void {
    if (this.pendingReload && !this.destroyed) {
      this.pendingReload = false;
      this.loadEvents();
    }
  }
    private loadMasterData() {
      Auth.currentSession().then(session => {
      const options = this.auth.createApiHeader(session);
        return this.commonUtil.apiGet('/myevent/master', options).then(res => {
          this.MasterData = res.data ?? {};
          this.masterLoaded = true;
        });
      }).catch(err => {
        this.commonUtil.debug().log(err);
        if (err.message === Constant.msgLoginFailed) {
          this.commonUtil.showReloadDialog(this.auth);
        } else {
          this.toastUtil.clearAllShowingToast();
          this.toastUtil.showErrorToast('', Constant.msgNetworkError, Constant.toastShowMiliSec);
        }
      });
    }

  getMasterTypeName(master_type: number): string {
    switch(master_type) {
      case 1: return '本部';
      case 2: return '支部';
      case 3: return 'エリア';
      default: return '';
    }
  }

  getDepartmentFromOrganizations(event: MyEventApproval): any {
    const orgs = event.drafter?.organizations;
    if (!orgs || orgs.length === 0) return null;

    return orgs[0];
  }

  /** カード表示用の開催日。期間は「〜」の代わりに「〇〇から」「〇〇まで」の2行にする */
  getCardDisplayDates(event: MyEvent | MyEventApproval): string[] {
    return this.getDisplayDates(event).flatMap(date => {
      const range = date.split('\n〜\n');
      return range.length === 2 ? [`${range[0]}から`, `${range[1]}まで`] : [date];
    });
  }

  /** 開催単位を「/」で区切った各部分（例：「支部/エリア」→［支部, エリア］）。PC幅の表で2行に分けて表示するため */
  splitHoldingUnit(unit: string | null): string[] {
    return unit ? unit.split('/') : [];
  }

  hasMoreDates(event: MyEvent | MyEventApproval): boolean {
    // 期間は開始日〜終了日で全体を表しているため「など」は付けない
    if (event.numberOfDays === 'period') {
      return false;
    }

    // schedules優先
    if (event.schedules?.length) {
      const uniqueDates = [
        ...new Set(event.schedules.map(s => s.eventDate))
      ];

      return uniqueDates.length > 3;
    }

    // eventDates
    return (event.eventDates?.length || 0) > 3;
  }

}
